import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Mail, 
  Phone, 
  MapPin,
  ArrowRight,
  Building2,
  Beaker,
  Target,
  Loader2,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    message: ''
  });
  const [submitStatus, setSubmitStatus] = useState(null); // 'loading' | 'success' | 'error'
  const [statusMessage, setStatusMessage] = useState('');

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus('loading');
    setStatusMessage('');

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      const data = await response.json();
      setSubmitStatus('success');
      setStatusMessage(data.message || 'Thank you! We will get back to you soon.');
      setFormData({ name: '', email: '', organization: '', message: '' });
    } catch (err) {
      setSubmitStatus('error');
      setStatusMessage('Something went wrong. Please try again or email us directly.');
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <Badge className="bg-[#E8E3DA] text-[#4A5D4E] border-none mb-4">
              <Mail className="w-3 h-3 mr-1" />
              Get In Touch
            </Badge>
            <h1 className="text-5xl font-bold text-[#2C3E2F] mb-6">
              Join Our Circular Journey
            </h1>
            <p className="text-xl text-[#5A6B5E] max-w-3xl mx-auto">
              Whether you're interested in partnerships, pilots, or simply want to learn more, 
              we'd love to hear from you.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 mb-16">
            {/* Contact Form */}
            <Card className="border-[#DDD5C7]">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-[#2C3E2F]">Name *</label>
                      <Input 
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Your name"
                        required
                        className="border-[#DDD5C7] focus:border-[#A8C9A5]"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-[#2C3E2F]">Email *</label>
                      <Input 
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="your@email.com"
                        required
                        className="border-[#DDD5C7] focus:border-[#A8C9A5]"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-[#2C3E2F]">Organization</label>
                    <Input 
                      name="organization"
                      value={formData.organization}
                      onChange={handleInputChange}
                      placeholder="Your organization"
                      className="border-[#DDD5C7] focus:border-[#A8C9A5]"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-[#2C3E2F]">Message *</label>
                    <Textarea 
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Tell us about your interest..."
                      required
                      rows={5}
                      className="border-[#DDD5C7] focus:border-[#A8C9A5]"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    size="lg" 
                    className="w-full bg-[#D4A574] hover:bg-[#C09460] text-white"
                    disabled={submitStatus === 'loading'}
                    data-testid="contact-submit-button"
                  >
                    {submitStatus === 'loading' ? (
                      <>
                        <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </Button>
                  {submitStatus === 'success' && (
                    <div data-testid="contact-success-message" className="flex items-center gap-2 text-green-700 bg-green-50 p-3 rounded-lg">
                      <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
                      <span className="text-sm">{statusMessage}</span>
                    </div>
                  )}
                  {submitStatus === 'error' && (
                    <div data-testid="contact-error-message" className="flex items-center gap-2 text-red-700 bg-red-50 p-3 rounded-lg">
                      <AlertCircle className="w-5 h-5 flex-shrink-0" />
                      <span className="text-sm">{statusMessage}</span>
                    </div>
                  )}
                </form>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-[#2C3E2F] mb-6">Contact Information</h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#A8C9A5]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-[#4A5D4E]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#2C3E2F] mb-1">Location</h3>
                      <p className="text-[#5A6B5E]">
                        Bangalore Bioinnovation Centre (BBC)<br />
                        Helix Biotech Park, IBAB Campus,<br />
                        Electronic City Phase 1,<br />
                        Bengaluru, Karnataka, India 560100
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#A8C9A5]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-[#4A5D4E]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#2C3E2F] mb-1">Email</h3>
                      <p className="text-[#5A6B5E]">rishov.biswas@punarvibes.com</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#A8C9A5]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-[#4A5D4E]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#2C3E2F] mb-1">Phone</h3>
                      <p className="text-[#5A6B5E]">+91 809 599 3409</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-[#F5F3EE] rounded-2xl p-6">
                <h3 className="font-semibold text-[#2C3E2F] mb-4">Office Hours</h3>
                <div className="space-y-2 text-[#5A6B5E]">
                  <p>Monday - Friday: 9:00 AM - 6:00 PM IST</p>
                  <p>Saturday: 10:00 AM - 2:00 PM IST</p>
                  <p>Sunday: Closed</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Opportunities */}
      <section className="py-20 px-6 bg-[#FDFBF7]">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#2C3E2F] mb-4">
              Partnership Opportunities
            </h2>
            <p className="text-lg text-[#5A6B5E] max-w-3xl mx-auto">
              We're building an ecosystem of partners committed to circular materials and climate-positive innovation.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Architects & Designers',
                description: 'Partner on pilot projects for sustainable interior materials',
                icon: Building2
              },
              {
                title: 'Research Institutions',
                description: 'Collaborate on material science and biofabrication research',
                icon: Beaker
              },
              {
                title: 'ESG Corporates',
                description: 'Integrate circular materials into your sustainability strategy',
                icon: Target
              }
            ].map((item) => (
              <Card key={item.title} className="border-[#DDD5C7] hover:shadow-lg transition-shadow">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-[#A8C9A5]/20 rounded-lg flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-[#4A5D4E]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#2C3E2F] mb-3">{item.title}</h3>
                  <p className="text-[#5A6B5E]">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
